import React, { useState, useEffect, useCallback } from 'react'
import {
  Row,
  Col,
  Button,
  Spinner,
  Table
} from 'react-bootstrap'
import * as RequisitionController from '../Controller/RequisitionController'

const ProductsTab = ({ 
  productsRange, 
  setProductsRange, 
  filters, 
  setFilters,
  onQuickRangeClick,
  activeTab
}) => {
  const [productCountData, setProductCountData] = useState([])
  const [productCountLoading, setProductCountLoading] = useState(false)

  const fetchProductCount = useCallback(async () => {
    try {
      setProductCountLoading(true)
      const response = await RequisitionController.getProductCountApi({
        from_created_date: filters.from_created_date,
        to_created_date: filters.to_created_date
      })
      // console.log("product---->",response)
      if (response?.success && response?.data) {
        setProductCountData(response.data)
      } else {
        setProductCountData([])
      }
    } catch (error) {
      console.error('Error fetching product count:', error)
      setProductCountData([])
    } finally {
      setProductCountLoading(false)
    }
  }, [filters.from_created_date, filters.to_created_date])

  // Fetch product count when date filters change or when products tab becomes active
  useEffect(() => {
    if (activeTab !== 'products') return
    fetchProductCount()
  }, [fetchProductCount, activeTab])

  const setQuickRange = (type) => {
    const today = new Date()
    let from = ''
    let to = ''
    const fmt = (d) => d.toISOString().split('T')[0]
    
    switch (type) {
      case 'today':
        from = fmt(today)
        to = fmt(today)
        break
      case 'next7': {
        const f = new Date(today)
        f.setDate(f.getDate() + 7)
        from = fmt(today)
        to = fmt(f)
        break
      }
      case 'nextMonth': {
        const f = new Date(today)
        f.setDate(f.getDate() + 30)
        from = fmt(today)
        to = fmt(f)
        break
      }
      case 'all':
        from = ''
        to = ''
        break
      default:
        break
    }
    
    // Update filters — useEffect will trigger fetchProductCount when filters change
    setFilters((prev) => ({
      ...prev,
      from_created_date: from,
      to_created_date: to,
      offset: 0
    }))
    
    // Update products range state
    setProductsRange(type)
  }

  const handleQuickRangeClick = (e) => {
    const range = e.currentTarget.getAttribute('data-range')
    if (range === 'custom') {
      const today = new Date()
      const fmt = (d) => d.toISOString().split('T')[0]
      setProductsRange('custom')
      setFilters((prev) => ({
        ...prev,
        from_created_date: fmt(today),
        to_created_date: prev.to_created_date || fmt(today),
        offset: 0
      }))
      return
    }
    if (range === 'today') return setQuickRange('today')
    if (range === 'next7') return setQuickRange('next7')
    if (range === 'nextMonth') return setQuickRange('nextMonth')
    if (range === 'all') return setQuickRange('all')
  }

  return (
    <>
      <Row className='mb-3 gx-2 searchBarPanel'>
        <Col className='d-flex align-items-center flex-wrap gap-2 innSearchBarPanel'>
          {productsRange === 'custom' && (
            <div className='d-flex align-items-center gap-2'>
              <input
                type='date'
                className='form-control form-control-sm'
                placeholder='dd.mm.yyyy'
                value={filters.from_created_date}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    from_created_date: e.target.value,
                    offset: 0
                  }))
                }
              />
              <span>to</span>
              <input
                type='date'
                className='form-control form-control-sm'
                placeholder='dd.mm.yyyy'
                value={filters.to_created_date}
                min={filters.from_created_date || new Date().toISOString().split('T')[0]}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    to_created_date: e.target.value,
                    offset: 0
                  }))
                }
              />
            </div>
          )}
          <div className='btn-group'>
            <Button
              size='sm'
              variant={productsRange === 'today' ? 'primary' : 'light'}
              data-range='today'
              onClick={handleQuickRangeClick}
            >
              Today
            </Button>
            <Button
              size='sm'
              variant={productsRange === 'next7' ? 'primary' : 'light'}
              data-range='next7'
              onClick={handleQuickRangeClick}
            >
              Next 7 Days
            </Button>
            <Button
              size='sm'
              variant={productsRange === 'nextMonth' ? 'primary' : 'light'}
              data-range='nextMonth'
              onClick={handleQuickRangeClick}
            >
              Next Month
            </Button>
            <Button
              size='sm'
              variant={productsRange === 'custom' ? 'primary' : 'light'}
              data-range='custom'
              onClick={handleQuickRangeClick}
            >
              Custom Dates
            </Button>
            <Button
              size='sm'
              variant={productsRange === 'all' ? 'primary' : 'light'}
              data-range='all'
              onClick={handleQuickRangeClick}
            >
              All
            </Button>
          </div>
        </Col>
      </Row>

      {productCountLoading ? (
        <div className='text-center py-4'>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              {/* <th>Product Code</th> */}
              <th>Product</th>
              <th>Total Quantity</th>
              <th>Unit of Measure</th>
            </tr>
          </thead>
          <tbody>
            {productCountData.length > 0 ? (
              productCountData
                .filter(product => product.product_details !== null && product.product_details !== undefined)
                .map((product, index) => (
                <tr key={index}>
                  {/* <td>{product.product_details?.product_code || product.product_id}</td> */}
                  <td>{product.product_details?.name  || ""} {product.product_details?.product_code ? `(${product.product_details?.product_code})` : ""}</td>
                  <td>{product.total_quantity}</td>
                  <td>{product.product_details?.unit_of_measure || 'N/A'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center">
                  No product data found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </>
  )
}

export default ProductsTab
